import React, { ReactElement } from 'react'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'

import { Select } from 'components/v2/Select'
import { Switch } from 'components/v2/Switch'
import { FieldArray, useField } from 'formik'
import styled from 'styled-components'
import SelectKreditsCategory from 'features/Kredits/components/SelectKreditsCategory'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { GTFrequencyUnit } from 'gql/types.generated'
import { safeFormatMessage } from 'utils/intl'
import { InputCurrency } from 'components/InputCurrency'
import { CloseIcon } from 'components/Icons'
import { IKreditsCategoriesForm } from 'features/Kredits/types'
import { useFeatureFlag } from '@10xlabs/hooks'
import { EFeatureFlagsNames } from 'config/feature-flags'

const DeleteButton = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const EmptyBox = styled.div`
  padding: 8px;
`

const frecuencyOptionMessages = defineMessages({
  [GTFrequencyUnit.Month]: {
    id: 'kredits.frequency.month',
    defaultMessage: 'Mes'
  },
  [GTFrequencyUnit.Day]: {
    id: 'kredits.frequency.week',
    defaultMessage: 'Dia'
  }
})

const TextCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`
const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
  justify-content: center;
`

interface KreditCategoryFormProps {
  fieldName: string
}

/**
 * KreditCategoryForm
 *
 * This component is used to render an array of objects that follow the `IKreditsCategoriesForm` interface within a Formik form.
 *
 * @param fieldName - The name of the field in the Formik form
 *
 * @remarks
 * The form containing this component must include a field that matches the `IKreditsCategoriesForm` interface.
 */

const KreditCategoryForm = ({ fieldName }: KreditCategoryFormProps): ReactElement => {
  const [kreditsCategoriesField] = useField<IKreditsCategoriesForm[]>(fieldName)

  return (
    <FieldArray name={fieldName}>
      {(arrayHelpers): ReactElement => {
        return (
          <>
            {kreditsCategoriesField.value.map((category, index) => {
              const rowFieldName = `${fieldName}[${index}]`
              return (
                <CategoryRow
                  excludedCategoryOptions={kreditsCategoriesField.value
                    .filter((_category) => _category.name !== category.name)
                    .map((category) => parseInt(category.name))}
                  showDeleteButton={kreditsCategoriesField.value.length > 1 && Boolean(category.isNew) && index > 0}
                  key={index}
                  fieldName={rowFieldName}
                  onDelete={(): void => {
                    arrayHelpers.remove(index)
                  }}
                />
              )
            })}
          </>
        )
      }}
    </FieldArray>
  )
}

interface CategoryRowProps {
  fieldName: string
  onDelete: () => void
  showDeleteButton?: boolean
  excludedCategoryOptions: number[]
}

const CategoryRow = ({
  fieldName,
  onDelete,
  showDeleteButton,
  excludedCategoryOptions
}: CategoryRowProps): ReactElement => {
  const intl = useIntl()
  const [categoryField] = useField<IKreditsCategoriesForm>(fieldName)
  const [frecuencyValueField, { error: frecuencyValueErrors, touched: frecuencyValueTouched }, frecuencyValueHelpers] =
    useField(`${fieldName}.frequency.value`)
  const [frecuencyUnitField, { error: frecuencyUnitErrors, touched: frecuencyUnitTouched }, frecuencyUnitHelpers] =
    useField(`${fieldName}.frequency.unit`)
  const [amountField, { error: amountErrors, touched: amountTouched }, amountFieldHelpers] = useField(
    `${fieldName}.amount`
  )
  const canEditName = categoryField.value.canEditName ?? true
  const [isShowingDayUnit] = useFeatureFlag(EFeatureFlagsNames.DayFrecuencyKreditsSettings)

  const frecuencyOptions = Object.values(GTFrequencyUnit).filter((unit) =>
    isShowingDayUnit ? true : unit !== GTFrequencyUnit.Day
  )
  return (
    <Row noMargin>
      <Column width="50%">
        <Row>
          <Column width="50%">
            <SelectKreditsCategory
              disabled={!canEditName}
              name={`${fieldName}.name`}
              excludedOptions={excludedCategoryOptions}
            />
          </Column>
          <Column width="50%">
            <Row>
              <Column width="50%">
                <TextCenter>
                  <FormattedMessage id="kredits.every" defaultMessage="Cada" />
                </TextCenter>
              </Column>
              <Column>
                <Select
                  name={`${fieldName}.frequency.value`}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
                    frecuencyValueHelpers.setValue(event.target.value)
                  }}
                  onBlur={frecuencyValueField.onBlur}
                  value={frecuencyValueField.value || 1}
                  errors={frecuencyValueErrors}
                  touched={frecuencyValueTouched}
                >
                  <option value="">
                    <FormattedMessage id="kredits.selectFrequencyValue" defaultMessage="Select" />
                  </option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </Select>
              </Column>
            </Row>
          </Column>
        </Row>
      </Column>

      <Column width="50%">
        <Row>
          <Column width="50%">
            <Select
              name={`${fieldName}.frequency.unit`}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
                frecuencyUnitHelpers.setValue(event.target.value)
              }}
              onBlur={frecuencyUnitField.onBlur}
              errors={frecuencyUnitErrors}
              touched={frecuencyUnitTouched}
              value={frecuencyUnitField.value}
            >
              <option value="">
                <FormattedMessage id="kredits.selectFrequency" defaultMessage="Frequency" />
              </option>
              {frecuencyOptions.map((unit) => (
                <option key={unit} value={unit}>
                  {safeFormatMessage({ intl, messages: frecuencyOptionMessages, key: unit })}
                </option>
              ))}
            </Select>
          </Column>
          <Column width="30%">
            <InputCurrency
              onBlur={amountField.onBlur}
              name={`${fieldName}.amount`}
              placeholder="$0.00"
              value={amountField.value}
              onChange={(value: string): void => {
                amountFieldHelpers.setValue(value)
              }}
              touched={amountTouched}
              errors={amountErrors}
              hideSuffix
            />
          </Column>
          <Column width="17%">
            <SwitchWrapper>
              <SwitchContainer>
                <Switch
                  value={false}
                  onChange={(): void => {
                    //TODO: Implementar
                  }}
                />
              </SwitchContainer>
              {showDeleteButton ? (
                <DeleteButton
                  onClick={(): void => {
                    onDelete()
                  }}
                  type="button"
                >
                  <CloseIcon />
                </DeleteButton>
              ) : (
                <EmptyBox />
              )}
            </SwitchWrapper>
          </Column>
        </Row>
      </Column>
    </Row>
  )
}
export default KreditCategoryForm
